import { memo, useMemo } from 'react';

import { Expand, Minimize } from 'lucide-react';
import moment from 'moment';

import { cn } from '@/shadcn/lib/utils';

import { AvatarComponent } from '@/components/ui/avatar/avatar';
import Ellipsis from '@/components/ui/utils/ellipsis';
import { useUser } from '@/hooks/useUser';
import type { Team } from '@/types/account.types';
import { TestId } from '@/types/testId';
import { parseEntities } from '../utils';

const WELCOME_MESSAGE =
  'Welcome back to your dashboard! All your monitoring information is here. You can see pending traces, your tasks and more.' as const;

const missingItemsInfo = (nbMissingItems: number): string =>
  `+ ${nbMissingItems} other team${nbMissingItems > 1 ? 's' : ''}`;

type Props = {
  className?: string;
  collapsed?: boolean;
  setCollapsed: (collapsed: boolean) => void;
};

// header of the user dashboard
export const UserDashboardHeader = memo(
  ({ className, collapsed = false, setCollapsed }: Props) => {
    const { user, loading } = useUser();

    if (loading) return null;

    const firstName = user?.firstName;
    const entities = user?.memberOf?.nodes || [];

    const { organization, teams } = useMemo(
      () => parseEntities(entities),
      [entities]
    );

    const { name: organizationName, avatar: organizationAvatar } =
      organization || {};

    return (
      <div
        className={cn(
          'relative flex h-[238px] flex-row flex-nowrap items-start justify-between overflow-hidden bg-cover bg-primary-dark bg-no-repeat p-7 text-white/90 transition-all duration-300 ease-out-cubic *:z-1',
          'before:absolute before:inset-0 before:bg-[url("/src/assets/IndigoTrace_Map.svg")] before:bg-center before:bg-cover before:bg-no-repeat before:opacity-30',
          collapsed && 'h-[103px] py-5',
          className
        )}
        data-collapsed={collapsed}
        data-testid={TestId.UserDashboardHeader.Dashboard}
      >
        <div className="flex h-full grow flex-row flex-nowrap overflow-hidden">
          <div
            className={cn(
              'w-[300px] shrink-0 overflow-hidden transition-[width] duration-200 ease-linear',
              collapsed && 'w-[220px]'
            )}
          >
            <div className="text-xl">Welcome back{firstName ? ',' : '!'}</div>
            {firstName && (
              <div
                className="font-semibold text-3xl"
                data-testid={TestId.UserDashboardHeader.FirstName}
              >{`${firstName}!`}</div>
            )}
            <div
              className={cn(
                'mt-4 text-sm opacity-100 transition-all duration-200 ease-out-quad',
                collapsed && 'h-0 opacity-0 blur-xs'
              )}
            >
              {WELCOME_MESSAGE}
            </div>
          </div>
          <div
            className={cn(
              'mx-8 flex h-full grow flex-col flex-nowrap overflow-hidden',
              collapsed && 'flex-row'
            )}
          >
            <div
              className={cn(
                'mr-3 mb-2 flex flex-col flex-nowrap justify-start overflow-hidden',
                collapsed && 'mr-12 mb-0'
              )}
            >
              <div className="mb-2 text-nowrap font-semibold text-base">
                Your organization
              </div>
              <div className="mr-4 mb-2 flex flex-row flex-nowrap items-center overflow-hidden">
                {organizationName ? (
                  <>
                    <AvatarComponent
                      name={organizationName}
                      size={collapsed ? 22 : 32}
                      source={organizationAvatar}
                    />
                    <div
                      className={cn(
                        'ml-3 truncate text-[25px]',
                        collapsed && 'text-sm'
                      )}
                      data-testid={TestId.UserDashboardHeader.OrganizationName}
                    >
                      {organizationName}
                    </div>
                  </>
                ) : (
                  <div className="text-sm">
                    You don&#39;t belong to any organization
                  </div>
                )}
              </div>
            </div>

            <div
              className={cn(
                'mr-3 flex grow flex-col flex-nowrap overflow-hidden',
                collapsed && 'mr-[50px]'
              )}
            >
              <div className="mb-2 font-semibold text-base">
                {`Your team${teams?.length > 1 ? 's' : ''}`}
              </div>
              {teams?.length > 0 ? (
                <Ellipsis
                  className="flex grow flex-row flex-wrap items-center"
                  infoClassName="pb-1 text-sm"
                  infoMessage={missingItemsInfo}
                  tooltipClassName="text-sm"
                >
                  {teams.map((team: Team) => {
                    const rowId = team.rowId;
                    const avatar = team.avatar;
                    const name = team.name;
                    return (
                      <div
                        className="mr-4 mb-2 flex flex-row flex-nowrap items-center overflow-hidden"
                        data-testid={TestId.UserDashboardHeader.Team}
                        key={rowId}
                      >
                        <AvatarComponent
                          name={name}
                          size={22}
                          source={avatar}
                        />
                        <div className="ml-3 truncate text-sm">{name}</div>
                      </div>
                    );
                  })}
                </Ellipsis>
              ) : (
                <div className="text-sm">You don&#39;t belong to any team</div>
              )}
            </div>
          </div>
        </div>
        <div
          className={cn(
            'flex w-[280px] shrink-0 flex-col flex-nowrap opacity-100 transition-all duration-200 ease-linear',
            collapsed && 'w-0 opacity-0 blur-xs'
          )}
        >
          <div className="mb-2 flex flex-row flex-nowrap items-center font-semibold text-base">
            Today
          </div>
          <div
            className="whitespace-nowrap font-light text-3xl"
            data-cy-hide
            data-testid={TestId.UserDashboardHeader.Today}
          >
            {moment().format('MMM. DD, YYYY')}
          </div>
        </div>
        <button
          className={cn(
            'absolute top-0 right-0 cursor-pointer p-6 text-white/90 outline-hidden transition-all duration-200 ease-in-out hover:scale-110',
            'before:-translate-x-1/2 before:-translate-y-1/2 before:absolute before:top-1/2 before:left-1/2 before:size-7 before:rounded-full before:bg-card/10 before:opacity-0 before:transition-all before:duration-100 before:ease-in-out hover:before:opacity-100'
          )}
          data-testid={TestId.UserDashboardHeader.CollapseButton}
          onClick={() => setCollapsed(!collapsed)}
          type="button"
        >
          {collapsed ? (
            <Expand className="stroke-2!" size={16} />
          ) : (
            <Minimize className="stroke-2!" size={16} />
          )}
        </button>
      </div>
    );
  }
);
